import { NewsletterPreferenceId } from "@components/_profile/Newsletter"
import { extractErrorCode, parseError } from "@lib/parse-error"
import { signIn } from "next-auth/react"
import { useRouter } from "next/router"
import { useState } from "react"
import { useCreateCheckoutSessionMutation } from "./use-create-checkout-session-mutation"
import { useSignupMutation } from "./use-signup-mutation"

export type SignupFormValues = {
  // We use these field names + hash to prevent spam bots
  // the values that we're using are the ones suffixed with the hash
  // if the smapbot fills in the ones without the hash, we know it's a spam bot and we deny the request
  // example: https://dev.to/felipperegazio/how-to-create-a-simple-honeypot-to-protect-your-web-forms-from-spammers--25n8
  firstNameHjJKLS: string
  lastNameHjJKLS: string
  emailHjJKLS: string
  password: string
  repeatPassword: string
  foundViesusThrough: string
  firstname: string
  lastname: string
  email: string
  passedCaptcha: boolean
  newsPreference: Record<NewsletterPreferenceId, boolean>
}

type UseSignupFormPayload = {
  submit: (values: SignupFormValues) => void
  status: "idle" | "loading"
  error: string | undefined
}

export function useSignupForm(): UseSignupFormPayload {
  const router = useRouter()
  const [error, setError] = useState<undefined | string>(undefined)
  const createCheckoutSessionMutation = useCreateCheckoutSessionMutation()
  const signupMutation = useSignupMutation()
  const isLoading =
    signupMutation.status === "pending" ||
    createCheckoutSessionMutation.status === "pending"

  async function submit(formValues: SignupFormValues) {
    const redirectToStripe = router.query && !!router.query.redirect_to_stripe
    const subscribedPlan = router.query?.subscribed_plan as string
    if (
      formValues.email !== "" ||
      formValues.firstname !== "" ||
      formValues.lastname ||
      !formValues.passedCaptcha
    ) {
      if (process.env.NODE_ENV === "development") {
        alert("Triggered spam protection")
      }
      return
    }
    try {
      const signup = await signupMutation.mutateAsync({
        firstName: formValues.firstNameHjJKLS,
        lastName: formValues.lastNameHjJKLS,
        email: formValues.emailHjJKLS,
        password: formValues.password,
        repeatPassword: formValues.repeatPassword,
        foundViesusThrough: formValues.foundViesusThrough,
        preferences: formValues.newsPreference,
      })
      if (signup) {
        await signIn("credentials", {
          password: formValues.password,
          email: formValues.emailHjJKLS,
          redirect: false,
        })

        if (redirectToStripe) {
          const result =
            await createCheckoutSessionMutation.mutateAsync(subscribedPlan)
          if (result?.createCheckoutSession.url)
            location.href = result.createCheckoutSession.url
        } else {
          router.push("/signup/success")
        }
      }
    } catch (error) {
      setError(parseError(extractErrorCode(error)))
    }
  }

  return { submit, error, status: isLoading ? "loading" : "idle" }
}
